import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { toast } from "sonner";
import { Sparkles, Briefcase, SlidersHorizontal } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { getCities, startSearch, getSearchStatus } from "@/lib/jobbkartaApi";
import { useProfile } from "@/components/jobbkarta/useProfile";
import Header from "@/components/jobbkarta/Header";
import Landing from "@/components/jobbkarta/Landing";
import MapPanel from "@/components/jobbkarta/MapPanel";
import ResultsList from "@/components/jobbkarta/ResultsList";
import DetailCard from "@/components/jobbkarta/DetailCard";
import ProfileDrawer from "@/components/jobbkarta/ProfileDrawer";
import EmailGeneratorDialog from "@/components/jobbkarta/EmailGeneratorDialog";
import GuideOfferDialog from "@/components/jobbkarta/GuideOfferDialog";
import Footer from "@/components/jobbkarta/Footer";
import { SearchProgress } from "@/components/jobbkarta/SearchProgress";

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

export default function Home() {
  const [cities, setCities] = useState([]);
  const [selectedCities, setSelectedCities] = useState([]);
  const [started, setStarted] = useState(false);

  const [data, setData] = useState({ companies: [], jobs: [], cities: [] });
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(null);
  const [eta, setEta] = useState(null);
  const [guideOfferOpen, setGuideOfferOpen] = useState(false);
  const pollSeq = useRef(0);

  const [view, setView] = useState("map");
  const [showSpontaneous, setShowSpontaneous] = useState(true);
  const [showJobAd, setShowJobAd] = useState(true);
  const [industry, setIndustry] = useState("all");

  const [selectedItem, setSelectedItem] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailTarget, setEmailTarget] = useState(null);

  const { profile, setProfile, isComplete } = useProfile();

  useEffect(() => {
    getCities()
      .then(setCities)
      .catch(() => toast.error("Kunde inte ladda städer."));
  }, []);

  const toggleCity = useCallback((city) => {
    setSelectedCities((prev) =>
      prev.some((c) => c.id === city.id)
        ? prev.filter((c) => c.id !== city.id)
        : [...prev, city]
    );
  }, []);

  const fetchResults = useCallback(async (cityObjs) => {
    if (cityObjs.length === 0) return;
    const seq = ++pollSeq.current;
    setLoading(true);
    setEta(null);
    setProgress({ status: "building", stage: "Startar sökning…", done: 0, total: 0, city: cityObjs[0]?.name });
    let enrichStart = null;
    try {
      const { job_id } = await startSearch(cityObjs);
      // Poll for progress until the search is done (or superseded / unmounted).
      let pollFails = 0;
      // eslint-disable-next-line no-constant-condition
      while (true) {
        if (seq !== pollSeq.current) return;
        let st;
        try {
          st = await getSearchStatus(job_id);
          pollFails = 0;
        } catch (pollErr) {
          // Tolerate transient hiccups (brief network blip / instance switch)
          // instead of failing the whole search on a single bad poll.
          pollFails += 1;
          if (pollFails >= 6) throw pollErr;
          await sleep(1200);
          continue;
        }
        if (seq !== pollSeq.current) return;
        setProgress(st);
        // rough ETA based on enrichment rate (done per second)
        if (st.total > 0) {
          if (enrichStart == null) enrichStart = Date.now();
          const elapsed = (Date.now() - enrichStart) / 1000;
          if (st.done > 0 && elapsed > 1) {
            const rate = st.done / elapsed;
            setEta(Math.max(1, Math.round((st.total - st.done) / rate)));
          }
        }
        if (st.status === "done") {
          setData({
            companies: st.companies || [],
            jobs: st.jobs || [],
            cities: st.cities || [],
          });
          if ((st.companies || []).length === 0) {
            toast.message("Hämtar fler företag i bakgrunden", {
              description: "Just nu hittades inga företag med e-post. Prova att söka på staden igen om en liten stund.",
            });
          }
          break;
        }
        await sleep(900);
      }
    } catch (e) {
      if (seq === pollSeq.current) toast.error("Kunde inte hämta jobb och företag.");
    } finally {
      if (seq === pollSeq.current) {
        setProgress(null);
        setEta(null);
        setLoading(false);
      }
    }
  }, []);

  const startExplore = () => {
    if (selectedCities.length === 0) return;
    setStarted(true);
    fetchResults(selectedCities);
  };

  // refetch when cities change after starting
  useEffect(() => {
    if (started && selectedCities.length > 0) {
      fetchResults(selectedCities);
    }
    if (started && selectedCities.length === 0) {
      setData({ companies: [], jobs: [], cities: [] });
    }
  }, [selectedCities]); // eslint-disable-line react-hooks/exhaustive-deps

  const allItems = useMemo(() => [...data.companies, ...data.jobs], [data]);

  const industries = useMemo(() => {
    const set = new Set();
    allItems.forEach((it) => it.industry && set.add(it.industry));
    return Array.from(set).sort();
  }, [allItems]);

  const filtered = useMemo(() => {
    return allItems.filter((it) => {
      if (it.type === "spontaneous" && !showSpontaneous) return false;
      if (it.type === "jobad" && !showJobAd) return false;
      if (industry !== "all" && it.industry !== industry) return false;
      return true;
    });
  }, [allItems, showSpontaneous, showJobAd, industry]);

  const mapCenter = useMemo(() => {
    if (data.cities.length > 0) {
      const c = data.cities[0];
      return [c.lat, c.lon];
    }
    return [62.0, 15.0];
  }, [data.cities]);

  const openDetail = (item) => {
    setSelectedItem(item);
    setDetailOpen(true);
  };

  const writeEmail = (company) => {
    setDetailOpen(false);
    setEmailTarget(company);
    setEmailOpen(true);
  };

  const spontaneousCount = data.companies.length;
  const jobCount = data.jobs.length;

  if (!started) {
    return (
      <>
        <Header
          compact={false}
          cities={cities}
          selected={selectedCities}
          onToggle={toggleCity}
          onOpenProfile={() => setProfileOpen(true)}
          onHome={() => setStarted(false)}
        />
        <Landing
          cities={cities}
          selected={selectedCities}
          onToggle={toggleCity}
          onStart={startExplore}
          onOpenProfile={() => setProfileOpen(true)}
        />
        <Footer />
        <ProfileDrawer
          open={profileOpen}
          onOpenChange={setProfileOpen}
          profile={profile}
          onSave={setProfile}
        />
      </>
    );
  }

  return (
    <div className="flex h-screen flex-col overflow-hidden">
      <Header
        compact
        cities={cities}
        selected={selectedCities}
        onToggle={toggleCity}
        view={view}
        onViewChange={setView}
        onOpenProfile={() => setProfileOpen(true)}
        onHome={() => setStarted(false)}
      />

      <div className="relative flex min-h-0 flex-1">
        {progress && progress.status !== "done" && (
          <SearchProgress progress={progress} etaSeconds={eta} />
        )}
        {/* Side panel */}
        <aside
          className={`${
            view === "list" ? "flex" : "hidden"
          } w-full flex-col border-r border-slate-200 bg-white md:flex md:w-[400px] md:shrink-0`}
        >
          {/* Filters */}
          <div className="space-y-3 border-b border-slate-100 p-3">
            <div className="flex gap-2">
              <button
                data-testid="filter-type-spontaneous"
                onClick={() => setShowSpontaneous((v) => !v)}
                className={`flex flex-1 items-center justify-center gap-1.5 rounded-xl border px-2 py-2 text-sm font-medium transition-colors ${
                  showSpontaneous
                    ? "border-emerald-500 bg-emerald-50 text-emerald-800"
                    : "border-slate-200 bg-white text-slate-400"
                }`}
              >
                <Sparkles className="h-4 w-4" /> Företag ({spontaneousCount})
              </button>
              <button
                data-testid="filter-type-jobad"
                onClick={() => setShowJobAd((v) => !v)}
                className={`flex flex-1 items-center justify-center gap-1.5 rounded-xl border px-2 py-2 text-sm font-medium transition-colors ${
                  showJobAd
                    ? "border-blue-500 bg-blue-50 text-blue-800"
                    : "border-slate-200 bg-white text-slate-400"
                }`}
              >
                <Briefcase className="h-4 w-4" /> Jobb ({jobCount})
              </button>
            </div>
            <div className="flex items-center gap-2">
              <SlidersHorizontal className="h-4 w-4 shrink-0 text-slate-400" />
              <Select value={industry} onValueChange={setIndustry}>
                <SelectTrigger data-testid="filter-industry-select" className="h-9 rounded-xl">
                  <SelectValue placeholder="Alla branscher" />
                </SelectTrigger>
                <SelectContent className="z-[1200]">
                  <SelectItem value="all">Alla branscher</SelectItem>
                  {industries.map((ind) => (
                    <SelectItem key={ind} value={ind}>
                      {ind}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="jk-scroll min-h-0 flex-1 overflow-y-auto">
            <ResultsList
              items={filtered}
              selectedId={selectedItem?.id}
              onSelect={openDetail}
              loading={loading}
            />
          </div>
        </aside>

        {/* Map */}
        <main
          className={`${
            view === "map" ? "block" : "hidden"
          } relative min-h-0 flex-1 md:block`}
        >
          <MapPanel
            items={filtered}
            center={mapCenter}
            cities={data.cities}
            selectedId={selectedItem?.id}
            onSelect={openDetail}
          />
          {/* Legend */}
          <div className="pointer-events-none absolute bottom-4 left-4 z-[500] space-y-1.5 rounded-xl border border-slate-200 bg-white/90 p-3 text-xs shadow-md backdrop-blur">
            <div className="flex items-center gap-2">
              <span className="h-3 w-3 rounded-full bg-emerald-600" /> Företag utan annons
            </div>
            <div className="flex items-center gap-2">
              <span className="h-3 w-3 rounded-full bg-blue-600" /> Utannonserat jobb
            </div>
          </div>
        </main>
      </div>

      <DetailCard
        item={selectedItem}
        open={detailOpen}
        onOpenChange={setDetailOpen}
        onWriteEmail={writeEmail}
      />
      <ProfileDrawer
        open={profileOpen}
        onOpenChange={setProfileOpen}
        profile={profile}
        onSave={setProfile}
      />
      <EmailGeneratorDialog
        open={emailOpen}
        onOpenChange={setEmailOpen}
        company={emailTarget}
        profile={profile}
        isProfileComplete={isComplete}
        onEditProfile={() => {
          setEmailOpen(false);
          setProfileOpen(true);
        }}
        onApplicationSent={() => {
          setTimeout(() => {
            setEmailOpen(false);
            setGuideOfferOpen(true);
          }, 500);
        }}
      />
      <GuideOfferDialog open={guideOfferOpen} onOpenChange={setGuideOfferOpen} />
    </div>
  );
}
